// src/client/components/Contact.jsx
import React, { useEffect, useMemo, useState } from "react";
import clientConfig from "../config/clientConfig";

const Contact = ({ theme }) => {
  const contact = clientConfig?.texts?.contact || {};

  const ui = contact?.ui || {};
  const labels = ui?.labels || {};
  const messages = ui?.messages || {};
  const aria = ui?.aria || {};
  const validation = ui?.validation || {};
  const maps = ui?.maps || {};

  const API_URL = process.env.REACT_APP_API_URL || "http://localhost:5050";
  const MAPS_KEY = process.env.REACT_APP_GOOGLE_MAPS_KEY;

  const c = (className) => `${theme}-${className}`;

  const [formData, setFormData] = useState({ name: "", email: "", message: "" });
  const [status, setStatus] = useState({ type: "", text: "" });
  const [csrfToken, setCsrfToken] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  // -----------------------------
  // 📍 Google Maps (no hardcode)
  // -----------------------------
  const address = contact?.address || "";

  const mapEmbedUrl = useMemo(() => {
    // 1) override complet (si le client veut une URL custom)
    if (maps?.embedUrlOverride) return maps.embedUrlOverride;
  
    // 2) pas d’adresse => rien à afficher
    if (!address) return "";
  
    // 3) si clé dispo => embed officiel Google Maps (Place)
    if (MAPS_KEY) {
      return `https://www.google.com/maps/embed/v1/place?key=${encodeURIComponent(
        MAPS_KEY
      )}&q=${encodeURIComponent(address)}`;
    }
  
    // 4) fallback sans clé => embed classique (fonctionne sans API key)
    return `https://www.google.com/maps?q=${encodeURIComponent(address)}&output=embed`;
  }, [MAPS_KEY, address, maps?.embedUrlOverride]);
  

  // -----------------------------
  // 🔐 CSRF
  // -----------------------------
  useEffect(() => {
    if (!API_URL) {
      setStatus({ type: "error", text: messages.configMissing || "" });
      return;
    }

    fetch(`${API_URL}/api/csrf-token`, { credentials: "include" })
      .then(async (res) => {
        if (!res.ok) throw new Error("csrf_failed");
        const data = await res.json();
        if (!data?.csrfToken) throw new Error("csrf_missing");
        setCsrfToken(data.csrfToken);
      })
      .catch(() => {
        setStatus({ type: "error", text: messages.security || "" });
      });
  }, [API_URL, messages.configMissing, messages.security]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((p) => ({ ...p, [name]: value }));
  };

  // -----------------------------
  // ✅ Validation configurable
  // -----------------------------
  const emailRegex = useMemo(() => {
    const pattern = validation?.emailRegex;
    if (!pattern) return null;
    try {
      return new RegExp(pattern);
    } catch {
      return null;
    }
  }, [validation?.emailRegex]);

  const requiredFields = useMemo(() => {
    return Array.isArray(validation?.requiredFields)
      ? validation.requiredFields
      : ["name", "email", "message"];
  }, [validation?.requiredFields]);

  const validate = () => {
    const missing = requiredFields.some((k) => !String(formData?.[k] || "").trim());
    if (missing) return messages.fillAll || "";

    const email = String(formData.email || "").trim();
    const okEmail = emailRegex ? emailRegex.test(email) : true;
    if (!okEmail) return messages.emailInvalid || "";

    const minLen = Number(validation?.minMessageLength ?? 10);
    if (Number.isFinite(minLen) && minLen > 0) {
      const msgLen = String(formData.message || "").trim().length;
      if (msgLen < minLen) return messages.messageTooShort || "";
    }

    return "";
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isSubmitting) return;

    setStatus({ type: "", text: "" });

    const error = validate();
    if (error) {
      setStatus({ type: "error", text: error });
      return;
    }

    if (!API_URL) {
      setStatus({ type: "error", text: messages.configMissing || "" });
      return;
    }

    if (!csrfToken) {
      setStatus({ type: "error", text: messages.security || "" });
      return;
    }

    setIsSubmitting(true);

    try {
      const res = await fetch(`${API_URL}/api/contact`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": csrfToken,
        },
        credentials: "include",
        body: JSON.stringify({
          name: String(formData.name || "").trim(),
          email: String(formData.email || "").trim(),
          message: String(formData.message || "").trim(),
        }),
      });

      if (res.ok) {
        setStatus({ type: "success", text: messages.success || "" });
        setFormData({ name: "", email: "", message: "" });
      } else {
        setStatus({ type: "error", text: messages.generic || "" });
      }
    } catch {
      setStatus({ type: "error", text: messages.network || "" });
    } finally {
      setIsSubmitting(false);
    }
  };

  const statusClass =
    status.type === "error"
      ? `${c("status")} ${c("status-error")}`
      : status.type === "success"
      ? `${c("status")} ${c("status-success")}`
      : c("status");

  const statusId = `${theme}-contact-status`;

  // -----------------------------
  // Helpers UI
  // -----------------------------
  const phoneHref = contact?.phone ? `tel:${String(contact.phone).replace(/\s+/g, "")}` : "";
  const emailHref = contact?.emailText ? `mailto:${contact.emailText}` : "";
  const directionsHref =
    address ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address)}` : "";

  const showQuickActions =
    (labels?.callAction && phoneHref) ||
    (labels?.emailAction && emailHref) ||
    (labels?.directionsAction && directionsHref);

  // =============================
  // ✅ THEME 2 — version vraiment différente
  // - hero contact split: map large + form card
  // - quick actions en haut (call / mail / itinéraire)
  // - details en colonne à droite + horaires en bloc
  // =============================
  if (theme === "theme2") {
    return (
      <section id="contact" className="theme2-contact-v2" aria-label={aria.sectionLabel || ""}>
        <div className="theme2-contact-v2-container">
          {contact.title ? <h2 className="theme2-contact-v2-title">{contact.title}</h2> : null}
          {ui.helpText ? <p className="theme2-contact-v2-sub">{ui.helpText}</p> : null}

          {showQuickActions ? (
            <div className="theme2-contact-v2-actions" aria-label={aria.actionsLabel || ""}>
              {labels?.directionsAction && directionsHref ? (
                <a className="theme2-contact-v2-action" href={directionsHref} target="_blank" rel="noreferrer">
                  <span aria-hidden="true">🧭</span> {labels.directionsAction}
                </a>
              ) : null}

              {labels?.callAction && phoneHref ? (
                <a className="theme2-contact-v2-action" href={phoneHref}>
                  <span aria-hidden="true">📞</span> {labels.callAction}
                </a>
              ) : null}

              {labels?.emailAction && emailHref ? (
                <a className="theme2-contact-v2-action" href={emailHref}>
                  <span aria-hidden="true">✉️</span> {labels.emailAction}
                </a>
              ) : null}
            </div>
          ) : null}

          <div className="theme2-contact-v2-grid">
            {/* MAP big */}
            <div className="theme2-contact-v2-map">
              {mapEmbedUrl ? (
                <iframe
                  title={aria.mapTitle || ""}
                  src={mapEmbedUrl}
                  loading="lazy"
                  className="theme2-contact-v2-map-frame"
                  referrerPolicy="no-referrer"
                />
              ) : ui.mapFallbackText ? (
                <div className="theme2-contact-v2-map-fallback">
                  <p>{ui.mapFallbackText}</p>
                </div>
              ) : null}

              {/* Details under map */}
              <div className="theme2-contact-v2-details">
                {contact.address ? (
                  <p>
                    <strong>{labels.addressLabel || ""}</strong>
                    <span className="theme2-contact-v2-muted"> {contact.address}</span>
                  </p>
                ) : null}

                {contact.phone ? (
                  <p>
                    <strong>{labels.phoneLabel || ""}</strong>
                    <span className="theme2-contact-v2-muted"> {contact.phone}</span>
                  </p>
                ) : null}

                {contact.emailText ? (
                  <p>
                    <strong>{labels.emailLabel || ""}</strong>
                    <span className="theme2-contact-v2-muted"> {contact.emailText}</span>
                  </p>
                ) : null}

                {contact.hours ? (
                  <div className="theme2-contact-v2-hours" aria-label={labels.hoursTitle || ""}>
                    {labels.hoursTitle ? <h3>{labels.hoursTitle}</h3> : null}
                    <pre>{contact.hours}</pre>
                  </div>
                ) : null}
              </div>
            </div>

            {/* FORM card */}
            <div className="theme2-contact-v2-formCard">
              <form
                onSubmit={handleSubmit}
                className="theme2-contact-v2-form"
                noValidate
                aria-busy={isSubmitting ? "true" : "false"}
              >
                <label className="theme2-contact-v2-label">
                  {labels.nameLabel || ""}
                  <input
                    name="name"
                    placeholder={labels.name || ""}
                    value={formData.name}
                    onChange={handleChange}
                    autoComplete="name"
                    required={requiredFields.includes("name")}
                    disabled={isSubmitting}
                    aria-describedby={status.text ? statusId : undefined}
                    aria-invalid={status.type === "error" ? "true" : "false"}
                  />
                </label>

                <label className="theme2-contact-v2-label">
                  {labels.emailLabelInline || ""}
                  <input
                    name="email"
                    type="email"
                    placeholder={labels.email || ""}
                    value={formData.email}
                    onChange={handleChange}
                    autoComplete="email"
                    required={requiredFields.includes("email")}
                    disabled={isSubmitting}
                    aria-describedby={status.text ? statusId : undefined}
                    aria-invalid={status.type === "error" ? "true" : "false"}
                  />
                </label>

                <label className="theme2-contact-v2-label">
                  {labels.messageLabel || ""}
                  <textarea
                    name="message"
                    placeholder={labels.message || ""}
                    value={formData.message}
                    onChange={handleChange}
                    required={requiredFields.includes("message")}
                    disabled={isSubmitting}
                    aria-describedby={status.text ? statusId : undefined}
                    aria-invalid={status.type === "error" ? "true" : "false"}
                    rows={Number(maps?.textareaRows ?? 6)}
                  />
                </label>

                <button className="theme2-contact-v2-submit" type="submit" disabled={isSubmitting}>
                  {isSubmitting ? labels.sending || "" : labels.button || ""}
                </button>

                {status.text ? (
                  <p id={statusId} className={statusClass} role="status" aria-live="polite">
                    {status.text}
                  </p>
                ) : null}
              </form>
            </div>
          </div>
        </div>
      </section>
    );
  }

  // =============================
  // ✅ THEME 1 — on garde ton layout,
  // mais on ajoute le plan Google (iframe) au lieu du “Carte indisponible…”
  // =============================
  if (theme === "theme1") {
    return (
      <section id="contact" className={c("contact")} aria-label={aria.sectionLabel || ""}>
        <div className={c("contact-container")}>
          <h2 className={c("title")}>{contact.title || ""}</h2>

          <div className={c("contact-flex")}>
            {/* FORM */}
            <div className={c("contact-form-wrapper")}>
              <form
                onSubmit={handleSubmit}
                className={c("contact-form")}
                noValidate
                aria-busy={isSubmitting ? "true" : "false"}
              >
                {ui.helpText ? <p className={c("contact-help")}>{ui.helpText}</p> : null}

                <input
                  name="name"
                  placeholder={labels.name || ""}
                  value={formData.name}
                  onChange={handleChange}
                  autoComplete="name"
                  required={requiredFields.includes("name")}
                  disabled={isSubmitting}
                  aria-describedby={status.text ? statusId : undefined}
                  aria-invalid={status.type === "error" ? "true" : "false"}
                />

                <input
                  name="email"
                  type="email"
                  placeholder={labels.email || ""}
                  value={formData.email}
                  onChange={handleChange}
                  autoComplete="email"
                  required={requiredFields.includes("email")}
                  disabled={isSubmitting}
                  aria-describedby={status.text ? statusId : undefined}
                  aria-invalid={status.type === "error" ? "true" : "false"}
                />

                <textarea
                  name="message"
                  placeholder={labels.message || ""}
                  value={formData.message}
                  onChange={handleChange}
                  required={requiredFields.includes("message")}
                  disabled={isSubmitting}
                  aria-describedby={status.text ? statusId : undefined}
                  aria-invalid={status.type === "error" ? "true" : "false"}
                  rows={Number(maps?.textareaRows ?? 6)}
                />

                <button type="submit" disabled={isSubmitting}>
                  {isSubmitting ? labels.sending || "" : labels.button || ""}
                </button>

                {status.text ? (
                  <p id={statusId} className={statusClass} role="status" aria-live="polite">
                    {status.text}
                  </p>
                ) : null}
              </form>
            </div>

            {/* MAP + INFOS */}
            <div className={c("contact-map-wrapper")}>
              {mapEmbedUrl ? (
                <iframe
                  title={aria.mapTitle || ""}
                  src={mapEmbedUrl}
                  loading="lazy"
                  className={c("map-frame")}
                  referrerPolicy="no-referrer"
                />
              ) : ui.mapFallbackText ? (
                <p className={c("map-fallback")} aria-live="polite">
                  {ui.mapFallbackText}
                </p>
              ) : null}

              <div className={c("contact-details")}>
                {contact.address ? (
                  <p>
                    {labels.addressLinePrefix || ""}{" "}
                    <strong>{labels.addressLabel || ""}</strong> {contact.address}
                  </p>
                ) : null}

                {contact.phone ? (
                  <p>
                    {labels.phoneLinePrefix || ""}{" "}
                    <strong>{labels.phoneLabel || ""}</strong> {contact.phone}
                  </p>
                ) : null}

                {contact.emailText ? (
                  <p>
                    {labels.emailLinePrefix || ""}{" "}
                    <strong>{labels.emailLabel || ""}</strong> {contact.emailText}
                  </p>
                ) : null}

                {contact.hours ? (
                  <div className={c("contact-hours")}>
                    <h3>{labels.hoursTitle || ""}</h3>
                    <pre>{contact.hours}</pre>
                  </div>
                ) : null}
              </div>
            </div>
          </div>
        </div>
      </section>
    );
  }

  // ✅ Default: ton rendu existant (autres thèmes)
  return (
    <section id="contact" className={c("contact")} aria-label={aria.sectionLabel || ""}>
      <div className={c("contact-container")}>
        <h2 className={c("title")}>{contact.title || ""}</h2>

        <div className={c("contact-flex")}>
          <div className={c("contact-form-wrapper")}>
            <form onSubmit={handleSubmit} className={c("contact-form")} noValidate>
              {ui.helpText ? <p className={c("contact-help")}>{ui.helpText}</p> : null}

              <input
                name="name"
                placeholder={labels.name || ""}
                value={formData.name}
                onChange={handleChange}
                disabled={isSubmitting}
              />
              <input
                name="email"
                type="email"
                placeholder={labels.email || ""}
                value={formData.email}
                onChange={handleChange}
                disabled={isSubmitting}
              />
              <textarea
                name="message"
                placeholder={labels.message || ""}
                value={formData.message}
                onChange={handleChange}
                disabled={isSubmitting}
                rows={Number(maps?.textareaRows ?? 6)}
              />

              <button type="submit" disabled={isSubmitting}>
                {isSubmitting ? labels.sending || "" : labels.button || ""}
              </button>

              {status.text ? <p className={statusClass}>{status.text}</p> : null}
            </form>
          </div>

          <div className={c("contact-map-wrapper")}>
            {mapEmbedUrl ? (
              <iframe
                title={aria.mapTitle || ""}
                src={mapEmbedUrl}
                loading="lazy"
                className={c("map-frame")}
                referrerPolicy="no-referrer"
              />
            ) : ui.mapFallbackText ? (
              <p className={c("map-fallback")}>{ui.mapFallbackText}</p>
            ) : null}

            <div className={c("contact-details")}>
              {contact.address ? (
                <p>
                  <strong>{labels.addressLabel || ""}</strong> {contact.address}
                </p>
              ) : null}
              {contact.phone ? (
                <p>
                  <strong>{labels.phoneLabel || ""}</strong> {contact.phone}
                </p>
              ) : null}
              {contact.emailText ? (
                <p>
                  <strong>{labels.emailLabel || ""}</strong> {contact.emailText}
                </p>
              ) : null}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact;
